import React from 'react';
import {
  Box,
  Button,
  EntityDrawer,
  Grid,
  HStack,
  Image,
  Spacer,
  VStack,
  Text,
} from '@wegroup/design-system';
import { Product } from '../../types/yellowHiveTypes';
import { useTranslation } from 'react-i18next';
import { FilePlus, Settings } from 'lucide-react';
import useHideHubspotBubble from '@common/src/hooks/support-chat/useHideHubspotBubble';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  product?: Product;
}

const ProductDetailDrawer: React.FC<Props> = ({ isOpen, product, onClose }) => {
  const { t } = useTranslation();
  useHideHubspotBubble();
  const isMailOnly =
    !!product?.ctaRequestUrl &&
    product.ctaRequestUrl.startsWith('mailto:') &&
    !product?.ctaManageUrl;

  const emailAddress = isMailOnly
    ? product?.ctaRequestUrl?.replace('mailto:', '')
    : undefined;

  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    if (!emailAddress) return;
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      // noop
    }
  };

  return (
    <EntityDrawer
      title={product?.name || 'Product'}
      rightElement={
        <HStack spacing="2">
          <Button size="sm" variant="grayedGhost" onClick={onClose}>
            {t('CLOSE')}
          </Button>
        </HStack>
      }
      isOpen={isOpen}
      onClose={onClose}
    >
      {product && (
        <VStack align="stretch" spacing="8">
          <Box maxW="xs" mx="auto">
            <Image src={product.logo} maxW="100%" objectFit="contain" />
          </Box>
          <p>{product.description}</p>
          <Spacer />
          {isMailOnly ? (
            <VStack align="stretch" spacing="3">
              <Text color="gray.600">
                Dit product is alleen via e-mail te regelen. Stuur je aanvraag
                naar het onderstaande adres.
              </Text>
              <HStack>
                <Box
                  as="code"
                  px="3"
                  py="2"
                  borderRadius="6px"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.100"
                >
                  {emailAddress}
                </Box>
                <Button onClick={handleCopy} variant="white">
                  {copied ? 'Gekopieerd' : 'Kopieer e-mail'}
                </Button>
              </HStack>
            </VStack>
          ) : (
            <Grid templateColumns="1fr 1fr" gap="2">
              <Button
                as="a"
                href={product.ctaRequestUrl}
                target="_blank"
                rel="noreferrer"
                leftIcon={<FilePlus size={18} />}
                variant="white"
                isDisabled={!product.ctaRequestUrl}
              >
                Verzekering aanvragen
              </Button>
              <Button
                as="a"
                href={product.ctaManageUrl || '#'}
                target="_blank"
                rel="noreferrer"
                leftIcon={<Settings size={18} />}
                variant="white"
              >
                Verzekering wijzigen
              </Button>
            </Grid>
          )}
        </VStack>
      )}
    </EntityDrawer>
  );
};

export default ProductDetailDrawer;
